import React, { useState, useEffect } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';
import DeleteModel from '../Common/DeleteModel';
import { getAllProviderFacilities, getProviderFacilityById, saveProviderFacility, deleteProviderFacility, getAllProviderSettings } from '../../Services/ProviderService';
import CustomDatePicker from '../Common/CustomDatePicker';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const STAFF_CATEGORY_SETTING_ID = 25;

const normalizeFacilityFormData = (item = {}) => ({
  ...item,
  facility_name: item?.facility_name ?? '',
  is_primary_affiliation: item?.is_primary_affiliation ?? null,
  start_date: item?.start_date ? String(item.start_date).split('T')[0] : '',
  end_date: item?.end_date ? String(item.end_date).split('T')[0] : '',
  currently_affiliated: item?.currently_affiliated ?? null,
  reason_for_leaving: item?.reason_for_leaving ?? '',
  accept_new_patients: item?.accept_new_patients ?? null,
  telemedicine: item?.telemedicine ?? null,
  take_calls: item?.take_calls ?? null,
  in_good_standing: item?.in_good_standing ?? null,
  staff_category: item?.staff_category === null || item?.staff_category === undefined || item?.staff_category === '' ? null : Number(item.staff_category),
  admitting_privileges: item?.admitting_privileges ?? null,
  department: item?.department ?? '',
  covering_physicians: item?.covering_physicians ?? '',
  approx_annual_patient_encounter: item?.approx_annual_patient_encounter ?? '',
  initial_appointment_date: item?.initial_appointment_date ? String(item.initial_appointment_date).split('T')[0] : '',
  last_appointment_date: item?.last_appointment_date ? String(item.last_appointment_date).split('T')[0] : '',
  appointment_end_date: item?.appointment_end_date ? String(item.appointment_end_date).split('T')[0] : '',
  reappointment_start_date: item?.reappointment_start_date ? String(item.reappointment_start_date).split('T')[0] : '',
  contact: item?.contact && typeof item.contact === 'object'
    ? {
        contact_name: item.contact.contact_name ?? '',
        contact_title: item.contact.contact_title ?? '',
        contact_email: item.contact.contact_email ?? '',
        contact_phone: item.contact.contact_phone ?? '',
        contact_fax: item.contact.contact_fax ?? '',
      }
    : {
        contact_name: '',
        contact_title: '',
        contact_email: '',
        contact_phone: '',
        contact_fax: '',
      },
  address: item?.address && typeof item.address === 'object'
    ? {
        street: item.address.street ?? '',
        street2: item.address.street2 ?? '',
        city: item.address.city ?? '',
        state: item.address.state ?? '',
        country: item.address.country ?? '',
        zipcode: item.address.zipcode ?? '',
      }
    : {
        street: '',
        street2: '',
        city: '',
        state: '',
        country: '',
        zipcode: '',
      },
  w9_practice_name: item?.w9_practice_name ?? '',
  tax_id: item?.tax_id ?? '',
  tax_id_type: item?.tax_id_type ?? '',
  npi: item?.npi ?? '',
});

const normalizeFacilityPayload = (payload = {}) => ({
  ...payload,
  facility_name: payload.facility_name ?? null,
  is_primary_affiliation: payload.is_primary_affiliation ?? null,
  start_date: payload.start_date || null,
  end_date: payload.end_date || null,
  currently_affiliated: payload.currently_affiliated ?? null,
  reason_for_leaving: payload.reason_for_leaving ?? null,
  accept_new_patients: payload.accept_new_patients ?? null,
  telemedicine: payload.telemedicine ?? null,
  take_calls: payload.take_calls ?? null,
  in_good_standing: payload.in_good_standing ?? null,
  staff_category: payload.staff_category === null || payload.staff_category === undefined || payload.staff_category === '' ? null : Number(payload.staff_category),
  admitting_privileges: payload.admitting_privileges ?? null,
  department: payload.department ?? null,
  covering_physicians: payload.covering_physicians ?? null,
  approx_annual_patient_encounter: payload.approx_annual_patient_encounter ?? null,
  initial_appointment_date: payload.initial_appointment_date || null,
  last_appointment_date: payload.last_appointment_date || null,
  appointment_end_date: payload.appointment_end_date || null,
  reappointment_start_date: payload.reappointment_start_date || null,
  contact_id: payload.contact_id ?? null,
  contact: payload.contact && typeof payload.contact === 'object' ? payload.contact : null,
  w9_practice_name: payload.w9_practice_name ?? null,
  tax_id: payload.tax_id ?? null,
  tax_id_type: payload.tax_id_type === null || payload.tax_id_type === undefined || payload.tax_id_type === '' ? null : Number(payload.tax_id_type),
  npi: payload.npi ?? null,
  address_id: payload.address_id ?? null,
  address: payload.address && typeof payload.address === 'object' ? payload.address : null,
  type: payload.type ?? null,
});

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

const emptyAffiliation = () => ({
  facility_name: '',
  is_primary_affiliation: null,
  start_date: null,
  end_date: null,
  currently_affiliated: null,
  reason_for_leaving: '',
  accept_new_patients: null,
  telemedicine: null,
  take_calls: null,
  in_good_standing: null,
  staff_category: null,
  admitting_privileges: null,
  department: '',
  covering_physicians: '',
  approx_annual_patient_encounter: '',
  initial_appointment_date: null,
  last_appointment_date: null,
  appointment_end_date: null,
  reappointment_start_date: null,
  contact: {
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    contact_fax: '',
  },
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  },
  w9_practice_name: '',
  tax_id: '',
  tax_id_type: '',
  npi: '',
});

export default function FacilityAffiliations({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [items, setItems] = useState(initialData?.affiliations || initialData?.facilityAffiliations || []);
  const [isOpen, setIsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [form, setForm] = useState(emptyAffiliation());
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [providerSettings, setProviderSettings] = useState({ staff_category: [] });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });

  const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';
  const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';

  const getOrganizationIdFromStorage = () => {
    try {
      return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read organization ID from localStorage:', error);
      return null;
    }
  };

  const getProviderIdFromStorage = () => {
    try {
      return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read provider ID from localStorage:', error);
      return null;
    }
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (initialData?.affiliations?.length > 0) return;

    let isMounted = true;
    const load = async () => {
      if (!isMounted) return;
      await loadFacilities();
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const loadFacilities = async () => {
    const effectiveProviderId = providerId || getProviderIdFromStorage();
    if (!token || !effectiveProviderId) return [];

    setLoading(true);
    try {
      const res = await getAllProviderFacilities(token, effectiveProviderId);
      if (res?.is_success) {
        const normalizedItems = Array.isArray(res.data) ? res.data.map(normalizeFacilityFormData) : [];
        setItems(normalizedItems);
        return normalizedItems;
      } else if (res?.is_success === false && res?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        // toast.error(res?.message || 'Failed to load facilities.');
      }
    } catch (error) {
      console.error('Failed to load facilities:', error);
    } finally {
      setLoading(false);
    }
    return [];
  };

  const loadProviderSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);

    try {
      const response = await getAllProviderSettings(token, STAFF_CATEGORY_SETTING_ID);
      if (response?.is_success) {
        const options = Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : [];
        setProviderSettings({ staff_category: options });
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal server error. Please contact support team.');
      }
    } catch (error) {
      console.error('Failed to load staff category settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const openAdd = () => {
    setForm(emptyAffiliation());
    setEditingIndex(-1);
    setIsOpen(true);
  };

  const openEdit = (index) => {
    const selectedItem = items[index];
    setForm(normalizeFacilityFormData({
      ...selectedItem,
      staff_category: selectedItem?.staff_category === null || selectedItem?.staff_category === undefined || selectedItem?.staff_category === ''
        ? null
        : Number(selectedItem.staff_category),
    }));
    setEditingIndex(index);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const handleChange = (path, value) => {
    if (path.includes('.')) {
      const [parent, child] = path.split('.');
      setForm((f) => ({ ...f, [parent]: { ...(f[parent] || {}), [child]: value } }));
    } else {
      setForm((f) => ({ ...f, [path]: value }));
    }
  };

  const handleSave = async () => {
    const effectiveProviderId = providerId || getProviderIdFromStorage();
    const organizationId = getOrganizationIdFromStorage();
    const facilityId = form.id || form.Id || form.facility_id || form.facility_affiliation_id || null;

    const payload = normalizeFacilityPayload({
      ...form,
      id: facilityId,
      staff_category: form.staff_category === null || form.staff_category === undefined || form.staff_category === '' ? null : Number(form.staff_category),
      provider_id: effectiveProviderId || form.provider_id || null,
      organization_id: organizationId || form.organization_id || null,
      type: facilityId ? 'update' : 'create',
    });

    if (!providerId) {
      const updated = [...items];
      if (editingIndex >= 0) updated[editingIndex] = payload;
      else updated.push(payload);
      setItems(updated);
      setIsOpen(false);
      setEditingIndex(-1);
      toast.success('Provider ID missing. Facility saved locally until provider is created.');
      if (onSaveSuccess) onSaveSuccess(updated);
      return;
    }

    if (!token) {
      toast.error('Unable to save facility without authentication.');
      return;
    }

    setLoading(true);
    try {
      const response = await saveProviderFacility(token, payload);
        if (response?.is_success) {
        const refreshed = await loadFacilities();
        if (!Array.isArray(refreshed)) {
          toast.error('Failed to refresh facility records after save');
          return;
        }
        setIsOpen(false);
        setEditingIndex(-1);
        if (onSaveSuccess) onSaveSuccess(refreshed);
        toast.success(response?.message || 'Facility affiliation saved successfully');
      } else if (!response?.is_success && response?.status_code === 500) {
        console.error('Internal Server Error. Please Contact Support Team.');
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        console.error('Failed to save facility affiliation.');
        toast.error(response?.message || 'Failed to save facility affiliation');
      }
    } catch (error) {
      console.error('Error saving facility affiliation:', error);
      toast.error(error?.message || 'Failed to save facility affiliation');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDeleteFacility(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDeleteFacility = async (index) => {
    const item = items[index];
    const optimisticUpdate = (updated) => {
      setItems(updated);
      if (onSaveSuccess) onSaveSuccess(updated);
    };

    if (token && item && (item.id || item.Id)) {
      try {
        const id = item.id || item.Id;
        const resp = await deleteProviderFacility(token, id);
        if (resp?.is_success) {
          const refreshed = await loadFacilities();
          if (Array.isArray(refreshed)) {
            optimisticUpdate(refreshed);
          } else {
            const updated = items.filter((_, i) => i !== index);
            optimisticUpdate(updated);
          }
          toast.success(resp?.message || 'Facility affiliation deleted successfully');
          return;
        } else if(!resp?.is_success && resp?.status_code === 500) {
          console.error('Internal Server Error. Please Contact Support Team.');
          toast.error('Internal Server Error. Please Contact Support Team.');
          return;
        } else{
          console.error('Failed to delete facility affiliation.');
          toast.error(resp?.message || 'Failed to delete facility affiliation');
          return;
        }
        return;
      } catch (err) {
        console.error('Error deleting facility:', err);
        toast.error('Failed to delete facility');
        return;
      }
    }

    optimisticUpdate(items.filter((_, i) => i !== index));
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Facility Affiliations</h6>
          <button type="button" className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2" onClick={openAdd}>
            <MdAdd size={18} /> Add
          </button>
        </div>

        {loading ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-4 text-muted small">
              No facility affiliations added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
            {
              items.map((it, idx) => (
                <React.Fragment key={idx}>
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Facility
                    </div>

                    <div className="fw-semibold text-dark">
                      {it.facility_name || 'Unnamed Facility'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                     Staff Category
                    </div>

                    <div className="fw-semibold text-dark">
                      {it.staff_category_name || '—'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Start Date
                    </div>

                    <div className="fw-semibold text-dark">
                    {it.start_date || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      End Date
                    </div>

                    <div className="fw-semibold text-dark">
                      {it.end_date || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEdit(idx)}><MdEdit size={16} /></button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => openDeleteModal(idx)}><MdDelete size={16} /></button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}

      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="facility affiliation"
        itemName={deleteModal.item?.facility_name || 'this facility affiliation'}
        description="This will permanently delete this facility affiliation. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      {isOpen && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fw-bold hc-text-primary m-0">{editingIndex >= 0 ? 'Edit Affiliation' : 'Add Affiliation'}</h5>
                <button type="button" className="btn-close" onClick={handleClose} />
              </div>
              <div className="modal-body p-4">
                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Basic Information</h6>
                  <div className="row g-3">
                    <div className="col-md-12 col-lg-8">
                      <label className="form-label fw-semibold">Facility Name <span className="text-danger">*</span></label>
                      <input className="form-control rounded-3 shadow-sm" value={form.facility_name} onChange={(e) => handleChange('facility_name', e.target.value)} />
                    </div>

                    <div className="col-md-12 col-lg-4">
                      <label className="form-label fw-semibold">Primary Affiliation</label>
                      <select
                        className="form-select rounded-3"
                        value={form.is_primary_affiliation === '' || form.is_primary_affiliation === null ? '' : String(!!form.is_primary_affiliation)}
                        onChange={(e) => handleChange('is_primary_affiliation', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Timeline & Status</h6>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">Start Date</label>
                      <CustomDatePicker
                        value={form.start_date}
                        onChange={(value) =>
                            handleChange(
                              "start_date",
                              value
                            )
                          }
                      />
                      {/* <input type="date" className="form-control rounded-3" 
                      value={form.start_date} onChange={(e) => handleChange('start_date', e.target.value)} /> */}
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">End Date</label>
                      <CustomDatePicker
                        value={form.end_date}
                        onChange={(value) =>
                            handleChange(
                              "end_date",
                              value
                            )
                          }
                      />
                      {/* <input type="date" className="form-control rounded-3" 
                      value={form.end_date} onChange={(e) => handleChange('end_date', e.target.value)} /> */}
                    </div>

                    <div className="col-md-12 col-lg-4">
                      <label className="form-label fw-semibold">Currently Affiliated</label>
                      <select
                        className="form-select rounded-3"
                        value={form.currently_affiliated === '' || form.currently_affiliated === null ? '' : String(!!form.currently_affiliated)}
                        onChange={(e) => handleChange('currently_affiliated', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-12">
                      <label className="form-label fw-semibold">Reason For Leaving</label>
                      <textarea className="form-control rounded-3" value={form.reason_for_leaving} onChange={(e) => handleChange('reason_for_leaving', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Facility Details</h6>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <SearchableDropdown
                        label="Staff Category"
                        placeholder="Select Option"
                        options={providerSettings.staff_category}
                        value={form.staff_category}
                        onChange={(value) => handleChange('staff_category', value === null || value === undefined || value === '' ? null : Number(value))}
                        disabled={settingsLoading}
                        loading={settingsLoading}
                        emptyMessage="No staff categories found"
                      />
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">Admitting Privileges</label>
                      <select
                        className="form-select rounded-3"
                        value={form.admitting_privileges === '' || form.admitting_privileges === null ? '' : String(!!form.admitting_privileges)}
                        onChange={(e) => handleChange('admitting_privileges', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">Accept New Patients</label>
                      <select
                        className="form-select rounded-3"
                        value={form.accept_new_patients === '' || form.accept_new_patients === null ? '' : String(!!form.accept_new_patients)}
                        onChange={(e) => handleChange('accept_new_patients', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">Telemedicine</label>
                      <select
                        className="form-select rounded-3"
                        value={form.telemedicine === '' || form.telemedicine === null ? '' : String(!!form.telemedicine)}
                        onChange={(e) => handleChange('telemedicine', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">Take Calls</label>
                      <select
                        className="form-select rounded-3"
                        value={form.take_calls === '' || form.take_calls === null ? '' : String(!!form.take_calls)}
                        onChange={(e) => handleChange('take_calls', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <label className="form-label fw-semibold">In Good Standing</label>
                      <select
                        className="form-select rounded-3"
                        value={form.in_good_standing === '' || form.in_good_standing === null ? '' : String(!!form.in_good_standing)}
                        onChange={(e) => handleChange('in_good_standing', e.target.value === 'true')}
                      >
                        <option value="">Select Option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="col-md-12 col-lg-6">
                      <label className="form-label fw-semibold">Approx Annual Patient Encounter</label>
                      <input className="form-control rounded-3" value={form.approx_annual_patient_encounter} onChange={(e) => handleChange('approx_annual_patient_encounter', e.target.value)} />
                    </div>

                    <div className="col-md-12 col-lg-6">
                      <label className="form-label fw-semibold">Covering Physicians</label>
                      <input className="form-control rounded-3" value={form.covering_physicians} onChange={(e) => handleChange('covering_physicians', e.target.value)} />
                    </div>

                  </div>
                </div>

                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Appointment Dates</h6>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-3">
                      <label className="form-label fw-semibold">Initial Appointment Date</label>
                      {/* <input type="date" className="form-control rounded-3" value={form.initial_appointment_date} onChange={(e) => handleChange('initial_appointment_date', e.target.value)} /> */}
                      <CustomDatePicker
                         value={form.initial_appointment_date}
                          onChange={(value) =>
                            handleChange(
                              "initial_appointment_date",
                              value
                            )
                          }
                      />
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <label className="form-label fw-semibold">Last Appointment Date</label>
                       <CustomDatePicker
                        value={form.last_appointment_date}
                          onChange={(value) =>
                            handleChange(
                              "last_appointment_date",
                              value
                            )
                          }
                       />   
                      {/* <input type="date" className="form-control rounded-3" 
                      value={form.last_appointment_date} 
                      onChange={(e) => handleChange('last_appointment_date', e.target.value)} /> */}
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <label className="form-label fw-semibold">Appointment End Date</label>
                       <CustomDatePicker
                            value={form.appointment_end_date}
                          onChange={(value) =>
                            handleChange(
                              "appointment_end_date",
                              value
                            )
                          }
                       /> 
                        
                      {/* <input type="date" className="form-control rounded-3" 
                      value={form.appointment_end_date} onChange={(e) => handleChange('appointment_end_date', e.target.value)} /> */}

                    </div>

                    <div className="col-md-6 col-lg-3">
                      <label className="form-label fw-semibold">Reappointment Start Date</label>
                      <CustomDatePicker
                        value={form.reappointment_start_date}
                        onChange={(value) =>
                            handleChange(
                              "reappointment_start_date",
                              value
                            )
                          }
                      />
                      {/* <input type="date" className="form-control rounded-3" 
                      value={form.reappointment_start_date} 
                      onChange={(e) => handleChange('reappointment_start_date', e.target.value)} /> */}
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Contact</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Contact Name</label>
                      <input className="form-control rounded-3" value={form.contact.contact_name} onChange={(e) => handleChange('contact.contact_name', e.target.value)} />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Contact Title</label>
                      <input className="form-control rounded-3" value={form.contact.contact_title} onChange={(e) => handleChange('contact.contact_title', e.target.value)} />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Contact Email</label>
                      <input className="form-control rounded-3" value={form.contact.contact_email} onChange={(e) => handleChange('contact.contact_email', e.target.value)} />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Contact Phone</label>
                      {/* <input className="form-control rounded-3" 
                      value={form.contact.contact_phone} onChange={(e) => handleChange('contact.contact_phone', e.target.value)} /> */}

                      <CustomPhoneInput
                        name="contact.contact_phone"
                        value={form.contact.contact_phone}
                        onChange={(value) =>
                          handleChange(
                            "contact.contact_phone",
                            value
                          )
                        }
                        defaultCountry="us"
                      />

                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Contact Fax</label>
                      <input className="form-control rounded-3" value={form.contact.contact_fax} onChange={(e) => handleChange('contact.contact_fax', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-bold hc-text-primary mb-3">Address</h6>
                  <AddressSelectorFields
                    title="Address"
                    fieldPrefix="address"
                    values={form.address}
                    errors={{}}
                    touched={{}}
                    onFieldChange={(fieldName, value) => handleChange(fieldName, value)}
                    renderAsterisk={() => <span className="text-danger">*</span>}
                  />
                </div>

                {/* Billing & Identifiers removed as requested */}
              </div>
              <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
                <button className="btn btn-light" onClick={handleClose} disabled={loading}>Cancel</button>
                <button className="btn hc-bg-primary text-white d-flex align-items-center justify-content-center" onClick={handleSave} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}
